import apiClient from '@/services/api/client';
import { ENDPOINTS } from '@/services/api/endpoints';
import { PackageTimeEntry, PackageTimeEntrySummary } from '@/types';

export interface CreatePackageTimeEntryDto {
  description?: string;
}

export interface CreateManualPackageTimeEntryDto {
  startTime: string;
  endTime: string;
  description?: string;
}

export interface FinalizePackageTimeEntryDto {
  description?: string;
}

export interface UpdatePackageTimeEntryDto {
  description?: string;
  startTime?: string;
  endTime?: string;
}

export const packageTimeEntriesService = {
  /**
   * Lista todos os apontamentos de horas de um pacote
   */
  async getByPackage(packageId: number): Promise<PackageTimeEntry[]> {
    const response = await apiClient.get<PackageTimeEntry[]>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.BASE(packageId)
    );
    return response.data;
  },

  /**
   * Busca o apontamento ativo do usuario logado para o pacote
   */
  async getActive(packageId: number): Promise<PackageTimeEntry | null> {
    const response = await apiClient.get<PackageTimeEntry | null>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.ACTIVE(packageId)
    );
    return response.data;
  },

  /**
   * Lista todos os apontamentos ativos do usuario logado
   */
  async getAllActive(): Promise<PackageTimeEntry[]> {
    const response = await apiClient.get<PackageTimeEntry[]>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.ACTIVE_ALL
    );
    return response.data;
  },

  /**
   * Inicia um novo cronometro de apontamento
   */
  async start(packageId: number, dto: CreatePackageTimeEntryDto): Promise<PackageTimeEntry> {
    const response = await apiClient.post<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.START(packageId),
      dto
    );
    return response.data;
  },

  /**
   * Adiciona apontamento manual (já finalizado)
   */
  async createManual(packageId: number, dto: CreateManualPackageTimeEntryDto): Promise<PackageTimeEntry> {
    const response = await apiClient.post<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.MANUAL(packageId),
      dto
    );
    return response.data;
  },

  /**
   * Pausa o cronometro
   */
  async pause(packageId: number, entryId: number): Promise<PackageTimeEntry> {
    const response = await apiClient.post<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.PAUSE(packageId, entryId)
    );
    return response.data;
  },

  /**
   * Retoma o cronometro pausado
   */
  async resume(packageId: number, entryId: number): Promise<PackageTimeEntry> {
    const response = await apiClient.post<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.RESUME(packageId, entryId)
    );
    return response.data;
  },

  /**
   * Finaliza o apontamento (irreversivel)
   */
  async finalize(
    packageId: number,
    entryId: number,
    dto: FinalizePackageTimeEntryDto
  ): Promise<PackageTimeEntry> {
    const response = await apiClient.post<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.FINALIZE(packageId, entryId),
      dto
    );
    return response.data;
  },

  /**
   * Exclui um apontamento nao finalizado
   */
  async delete(packageId: number, entryId: number): Promise<void> {
    await apiClient.delete(ENDPOINTS.PACKAGE_TIME_ENTRIES.DELETE(packageId, entryId));
  },

  /**
   * Atualiza um apontamento existente
   */
  async update(packageId: number, entryId: number, dto: UpdatePackageTimeEntryDto): Promise<PackageTimeEntry> {
    const response = await apiClient.put<PackageTimeEntry>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.UPDATE(packageId, entryId),
      dto
    );
    return response.data;
  },

  /**
   * Retorna o resumo de horas do pacote
   */
  async getSummary(packageId: number): Promise<PackageTimeEntrySummary> {
    const response = await apiClient.get<PackageTimeEntrySummary>(
      ENDPOINTS.PACKAGE_TIME_ENTRIES.SUMMARY(packageId)
    );
    return response.data;
  },
};
